/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useCallback, useState } from 'react'

import { Button, Flex, Image } from 'antd'
import { HStack, Icon, T, Tooltip } from 'govwell-ui'

import FileNameInput from 'src/components/FileUpload/states/FileNameInput'
import { useRenameFileMutation } from 'src/fetch/files'
import useDisclosure from 'src/hooks/use-disclosure'
import { KeyboardKey } from 'src/types'
import { openUrlInNewTab } from 'src/utils'
import { isImage, isPdf } from 'src/utils/file'

type Props = {
  id: number | undefined
  name: string
  onRemoveFile: () => void
  onRenameFile: (value: string) => void
  thumbnailUrl: string | undefined
  type: string | undefined
  url: string | undefined
}

export const Done = (props: Props) => {
  const { id, name: originalFileName, onRenameFile, onRemoveFile, thumbnailUrl, type, url } = props
  const [fileName, setFileName] = useState(originalFileName)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const editNameState = useDisclosure()
  const previewModalState = useDisclosure()
  const imageSrc = thumbnailUrl ?? url
  const showImage = type && isImage(type) && imageSrc

  const handleImageClicked = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      previewModalState.toggle()
    },
    [previewModalState]
  )

  const handleNameClicked = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      editNameState.open()
    },
    [editNameState]
  )

  const handleNamePressed = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === KeyboardKey.Enter || e.key === KeyboardKey.Space) {
        e.preventDefault()
        e.stopPropagation()
        editNameState.open()
      }
    },
    [editNameState]
  )

  const handleNameEditsCancelled = useCallback(() => {
    editNameState.close()
  }, [editNameState])

  const { mutateAsync: renameFile, isPending: isRenamingFile } = useRenameFileMutation()
  const handleNameEditsSaved = useCallback(async () => {
    if (!id) {
      return
    }
    if (fileName !== originalFileName) {
      await renameFile({ id, newName: fileName })
      onRenameFile(fileName)
    }
    editNameState.close()
  }, [editNameState, fileName, id, onRenameFile, originalFileName, renameFile])

  const handleRemoveClicked = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      onRemoveFile()
    },
    [onRemoveFile]
  )

  const handleViewFileClicked = useCallback(() => {
    if (!url) {
      return
    }
    openUrlInNewTab(url)
  }, [url])

  return (
    <Flex gap="16px" align="center" justify="space-between" style={{ width: '100%' }}>
      <Flex gap="12px" align="center" flex={1}>
        <Flex
          onClick={handleImageClicked}
          style={{ minWidth: '40px', position: 'relative' }}
          justify="center"
        >
          <Tooltip content={url ? 'View file' : undefined}>
            <Button
              type="text"
              icon={
                !showImage ? (
                  <Icon
                    icon={type && isPdf(type) ? 'FaFilePdf' : 'FaFile'}
                    className="text-2xl text-green-600"
                  />
                ) : null
              }
              onClick={handleViewFileClicked}
              className="!p-0"
              aria-label="View file"
            >
              {showImage && (
                <HStack minHeight="30px">
                  <Image
                    style={{
                      maxWidth: '30px',
                      maxHeight: '30px',
                    }}
                    src={imageSrc}
                    alt={originalFileName}
                    preview={false}
                  />
                </HStack>
              )}
            </Button>
          </Tooltip>
        </Flex>
        {editNameState.isOpen ? (
          <FileNameInput
            errorMessage={errorMessage}
            fileName={fileName}
            isSaving={isRenamingFile}
            onErrorMessageChange={setErrorMessage}
            onFileNameChange={setFileName}
            onCancel={handleNameEditsCancelled}
            onSave={handleNameEditsSaved}
            size="sm"
            width="100%"
            shouldRevertInvalidFileNameOnBlur
          />
        ) : (
          <button
            onClick={handleNameClicked}
            onKeyDown={handleNamePressed}
            className="group outline-outline flex cursor-pointer items-start justify-start gap-1 rounded-xs focus-visible:outline"
          >
            <T.Body
              data-testid="file-upload-done-name"
              maxLines={1}
              as="span"
              className="text-left"
            >
              {fileName}
            </T.Body>
            <T.Body as="span">
              <Icon.FaPen className="text-left opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />
            </T.Body>
          </button>
        )}
      </Flex>
      <HStack gap={1} className="flex-0">
        <Tooltip content="Remove file">
          <Button
            data-testid="file-upload-remove-btn"
            type="text"
            onClick={handleRemoveClicked}
            icon={<Icon.FaTrash />}
            size="small"
          />
        </Tooltip>
      </HStack>
    </Flex>
  )
}
